import {DeepReadonly, readonly, ref, Ref} from 'vue'
import log from './log'

export type ConnectionState = 'disconnected' | 'connecting' | 'authenticating' | 'connected'

type MessageHandler = (msg: Record<string, unknown>) => void

export class WebSocketClient {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private serverPingTimeout: ReturnType<typeof setTimeout> | null = null
  private serverPingTimeoutMs = 45000

  private messageHandlers = new Map<string, Set<MessageHandler>>()
  private authenticatedHandlers = new Set<() => void>()

  private _state = ref<ConnectionState>('disconnected')

  constructor(
    private readonly serverHost: string,
    private readonly getToken: () => string | undefined
  ) {}

  get state(): DeepReadonly<Ref<ConnectionState>> {
    return readonly(this._state)
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return
    }

    this._state.value = 'connecting'

    const protocol = 'wss'
    const url = `${protocol}://${this.serverHost}/core/ws`

    log.debug(`[WS] Connecting to ${url}`)

    try {
      this.ws = new WebSocket(url)
      this.setupHandlers()
    } catch (error) {
      log.error('[WS] Failed to create WebSocket:', error)
      this.handleDisconnect()
    }
  }

  send(message: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    }
  }

  sendAuth(): void {
    this.send({ type: 'auth', token: this.getToken() })
  }

  on(type: string, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set())
    }
    this.messageHandlers.get(type)!.add(handler)
    return () => this.messageHandlers.get(type)?.delete(handler)
  }

  onAuthenticated(handler: () => void): () => void {
    this.authenticatedHandlers.add(handler)
    return () => this.authenticatedHandlers.delete(handler)
  }

  private setupHandlers(): void {
    if (!this.ws) return

    this.ws.onopen = () => {
      log.debug('[WS] Connected, authenticating...')
      this.reconnectAttempts = 0
      this._state.value = 'authenticating'
      this.send({ type: 'auth', token: this.getToken() })
    }

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as Record<string, unknown>
        this.handleMessage(message)
      } catch (error) {
        log.error('[WS] Failed to parse message:', error)
      }
    }

    this.ws.onerror = (event) => {
      log.error('[WS] WebSocket error:', event)
    }

    this.ws.onclose = (event) => {
      log.debug(`[WS] Closed: code=${event.code}, reason=${event.reason}`)
      if (this.isPermanentlyRejected(event.code)) {
        this.handlePermanentFailure(event.reason || `close code ${event.code}`)
      } else {
        this.handleDisconnect()
      }
    }
  }

  private handleMessage(message: Record<string, unknown>): void {
    const type = message.type as string

    if (type === 'authenticated') {
      this._state.value = 'connected'
      this.resetPingWatchdog()
      log.debug('[WS] Authenticated')
      this.authenticatedHandlers.forEach(handler => {
        try { handler() } catch (error) { log.error('[WS] Authenticated handler error:', error) }
      })
      return
    }

    if (type === 'ping') {
      this.send({ type: 'pong' })
      this.resetPingWatchdog()
      return
    }

    if (type === 'error') {
      log.error('[WS] Server error:', message.code, message.message)
      if (message.code === 'AUTH_FAILED') {
        this.handlePermanentFailure(message.message as string)
      } else if (message.code === 'AUTH_REQUIRED') {
        this.handleDisconnect()
      }

      if (message.liveQueryId) {
        const handlers = this.messageHandlers.get('error')
        if (handlers) {
          handlers.forEach(handler => {
            try { handler(message) } catch (error) { log.error('[WS] Error handler error:', error) }
          })
        }
      }
      return
    }

    if (type === 'warning') {
      log.warn('[WS] Server warning:', message.code, message.message)
      return
    }

    const handlers = this.messageHandlers.get(type)
    if (handlers) {
      handlers.forEach(handler => {
        try { handler(message) } catch (error) { log.error(`[WS] Handler error for type '${type}':`, error) }
      })
    }
  }

  private handleDisconnect(): void {
    this.clearTimers()
    this.ws = null

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = this.getReconnectDelay()
      log.debug(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`)
      this._state.value = 'disconnected'
      this.reconnectTimer = setTimeout(() => {
        this.reconnectAttempts++
        this.connect()
      }, delay)
    } else {
      log.error('[WS] Max reconnect attempts reached')
      this._state.value = 'disconnected'
    }
  }

  private isPermanentlyRejected(code: number): boolean {
    // 1008 = policy violation (auth failed), 4001 = auth timeout, 4002 = ping timeout, 4003 = max connections
    return code === 1008 || code === 4001 || code === 4002 || code === 4003
  }

  private handlePermanentFailure(reason: string): void {
    this.clearTimers()
    this.ws = null
    this._state.value = 'disconnected'
    log.error(`[WS] Connection permanently rejected, not reconnecting: ${reason}`)
  }

  private getReconnectDelay(): number {
    const baseDelay = 1000
    const maxDelay = 30000
    const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts), maxDelay)
    return delay + delay * 0.1 * Math.random()
  }

  private resetPingWatchdog(): void {
    if (this.serverPingTimeout) clearTimeout(this.serverPingTimeout)
    this.serverPingTimeout = setTimeout(() => {
      log.warn('[WS] Server ping timeout, reconnecting...')
      this.ws?.close(4000, 'Server ping timeout')
    }, this.serverPingTimeoutMs)
  }

  private clearTimers(): void {
    if (this.serverPingTimeout) { clearTimeout(this.serverPingTimeout); this.serverPingTimeout = null }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
  }
}
